import { useTheme } from '@canopysecurity/component-library';
import { LoadingButton } from '@mui/lab';
import Papa from 'papaparse';
import React, { useCallback, useMemo, useState } from 'react';
import { useAssociateTagWithDeviceMutation } from '../../gql/generated';
import { FormInput, FormLabelContainer } from '../../StyledComponents/Form';
import { ParagraphText } from '../../StyledComponents/Typography';
import { ModalContent } from './ModalContent';
import { TagKnown } from '../../Pages/ManageTags/TagsTable';

interface AssociateDeviceTagPanelProps {
  tag: TagKnown;
  onClose: () => void;
}

export const AssociateDeviceTagPanel = (
  props: AssociateDeviceTagPanelProps,
) => {
  const theme = useTheme();
  const { tag, onClose } = props;
  const [file, setFile] = useState<File | null>(null);
  const [serialNumber, setSerialNumber] = useState<string>('');
  const [serialNumbers, setSerialNumbers] = useState<string[]>([]);

  const allSerialNumbers = useMemo(
    () =>
      serialNumbers.length ? serialNumbers : serialNumber ? [serialNumber] : [],
    [serialNumber, serialNumbers],
  );

  const handleSelectFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        Papa.parse(event.target.files[0], {
          header: false,
          skipEmptyLines: true,
          complete(results) {
            const serialNumberArray = results.data
              .filter(Boolean)
              .flat() as string[];
            setSerialNumbers(serialNumberArray);
          },
        });
      }
    },
    [],
  );
  const [associateTagWithDevice, { loading, error: associateTagError }] =
    useAssociateTagWithDeviceMutation();

  const handleAssociateTag = async () => {
    if (tag.tagId) {
      const associations = allSerialNumbers
        .filter((sn) => sn.length)
        .map(async (sn) =>
          associateTagWithDevice({
            variables: {
              input: {
                tagId: tag.tagId,
                serialNumber: sn,
                tagName: tag.tagName,
              },
            },
          }),
        );
      await Promise.all(associations);
    }
    onClose();
  };

  return (
    <ModalContent
      title={`Associate Device(s) to Tag: ${tag.tagName}`}
      loading={loading}
      error={associateTagError}
      handleClose={onClose}
      submitDisabled={loading || !allSerialNumbers.length}
      handleSubmit={handleAssociateTag}
      buttonLabels={{ loading: 'Associating...', create: 'Associate' }}
    >
      <FormLabelContainer>
        <FormInput
          placeholder="Enter a serial number"
          maxLength={120}
          defaultValue={serialNumber}
          autoCapitalize="none"
          onChange={(event): void => {
            setFile(null);
            setSerialNumbers([]); // ?
            setSerialNumber(event.target.value);
          }}
        />
      </FormLabelContainer>
      <ParagraphText>OR</ParagraphText>
      <LoadingButton
        variant="outlined"
        component="label"
        style={{
          color: theme.primary.main,
          borderColor: theme.primary.main,
        }}
      >
        Upload CSV{file ? ` (${file.name})` : ''}
        <input type="file" hidden accept=".csv" onChange={handleSelectFile} />
      </LoadingButton>
    </ModalContent>
  );
};
